import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import './NotificationsPage.css';

const NotificationsPage = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      const res = await api.get('/notifications');
      setAnnouncements(Array.isArray(res.data) ? res.data : []);
      setLoadError(false);
    } catch (error) {
      // Uma falha de rede não é «não tem notificações»: diz-se o que aconteceu e deixa-se repetir.
      console.error('Erro ao carregar notificações:', error);
      setAnnouncements([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async (id) => {
    try {
      // Sem filtro por sinal: as mensagens globais chegam com Id negativo e também se guardam
      // como lidas no servidor (antes o Id negativo era ignorado aqui e a mensagem voltava a
      // aparecer por ler em cada recarregamento).
      await api.post(`/notifications/${id}/dismiss`);
      setAnnouncements(prev => {
        const arr = Array.isArray(prev) ? prev : [];
        return arr.map(a => a.id === id ? { ...a, isDismissed: true } : a);
      });
      toast.success('Notificação marcada como lida');
    } catch (error) {
      toast.error('Erro ao marcar notificação');
      loadAnnouncements(); // o ecrã não fica a dizer «lida» quando o servidor não a guardou
    }
  };

  const handleDismissAll = async () => {
    try {
      await api.post('/notifications/dismiss-all');
      toast.success('Todas as notificações marcadas como lidas');
      loadAnnouncements(); // relê do servidor: é ele que sabe o que ficou realmente por ler
    } catch (error) {
      toast.error('Erro ao marcar notificações');
    }
  };

  const getTypeStyles = (type) => {
    switch (type?.toLowerCase()) {
      case 'warning':
        return { icon: '⚠️', bgColor: 'var(--warning-bg)', textColor: 'var(--warning-text)', borderColor: 'var(--warning)' };
      case 'success':
        return { icon: '✅', bgColor: 'var(--success-bg)', textColor: 'var(--success-text)', borderColor: 'var(--success)' };
      case 'promo':
        return { icon: '🎉', bgColor: 'var(--primary-light)', textColor: 'var(--accent-secondary)', borderColor: 'var(--accent-secondary)' };
      default:
        return { icon: '📢', bgColor: 'var(--primary-light)', textColor: 'var(--accent-primary)', borderColor: 'var(--accent-primary)' };
    }
  };

  const safeAnnouncements = Array.isArray(announcements) ? announcements : [];
  
  const filteredAnnouncements = filter === 'unread' 
    ? safeAnnouncements.filter(a => !a.isDismissed)
    : safeAnnouncements;

  const unreadCount = safeAnnouncements.filter(a => !a.isDismissed).length;

  if (loading) {
    return (
      <DashboardLayout userType="client">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>A carregar...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="client">
      <div className="notifications-page">
        <div className="notifications-header">
          <div className="notifications-title">
            <h2>🔔 Notificações</h2>
            {unreadCount > 0 && (
              <span className="unread-badge">{unreadCount} não lidas</span>
            )}
          </div>
          <div className="notifications-actions">
            <div className="filter-tabs">
              <button 
                className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                Todas
              </button>
              <button 
                className={`filter-tab ${filter === 'unread' ? 'active' : ''}`}
                onClick={() => setFilter('unread')}
              >
                Não lidas
              </button>
            </div>
            {unreadCount > 0 && (
              <button className="mark-all-btn" onClick={handleDismissAll}>
                ✓ Marcar todas como lidas
              </button>
            )}
          </div>
        </div>

        <div className="notifications-list">
          {loadError ? (
            <div className="empty-state" style={{ padding: '60px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div>
              <h3 style={{ marginBottom: '10px', color: 'var(--text-primary)' }}>
                Não foi possível carregar as notificações
              </h3>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                A ligação à API falhou. Isto não quer dizer que não tem notificações.
              </p>
              <button className="mark-all-btn" onClick={loadAnnouncements}>Tentar de novo</button>
            </div>
          ) : filteredAnnouncements.length === 0 ? (
            <div className="empty-state" style={{ padding: '60px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '64px', marginBottom: '20px' }}>📬</div>
              <h3 style={{ marginBottom: '10px', color: 'var(--text-primary)' }}>
                {filter === 'unread' ? 'Todas as notificações lidas!' : 'Sem notificações'}
              </h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                {filter === 'unread' 
                  ? 'Não tem notificações por ler de momento.'
                  : 'Será avisado quando houver novidades!'
                }
              </p>
            </div>
          ) : (
            filteredAnnouncements.map(announcement => {
              const styles = getTypeStyles(announcement.type);
              return (
                <div 
                  key={announcement.id} 
                  className={`notification-card ${announcement.isDismissed ? 'read' : 'unread'}`}
                  style={{ 
                    backgroundColor: announcement.backgroundColor || styles.bgColor,
                    borderLeftColor: styles.borderColor
                  }}
                >
                  <div className="notification-icon">
                    {announcement.icon || styles.icon}
                  </div>
                  <div className="notification-content">
                    <div className="notification-header">
                      <h4 style={{ color: announcement.textColor || styles.textColor }}>
                        {announcement.title}
                      </h4>
                      <span className="notification-time">
                        {format(new Date(announcement.createdAt), "dd MMM 'às' HH:mm", { locale: pt })}
                      </span>
                    </div>
                    {announcement.content && (
                      <p className="notification-message">{announcement.content}</p>
                    )}
                    <div className="notification-footer">
                      {announcement.scope === 'global' && (
                        <span className="scope-badge global">🌐 Aviso Global</span>
                      )}
                      {announcement.actionUrl && (
                        <a href={announcement.actionUrl} className="notification-action">
                          {announcement.actionText || 'Ver mais'} →
                        </a>
                      )}
                    </div>
                  </div>
                  {!announcement.isDismissed && (
                    <button 
                      className="dismiss-btn"
                      onClick={() => handleDismiss(announcement.id)}
                      title="Marcar como lida"
                    >
                      ✓
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NotificationsPage;
